import { useCallback, useState } from 'react';
import { ArticleShelf } from '../components/knowledge/ArticleShelf';
import { KnowledgeSpace } from '../components/knowledge/KnowledgeSpace';
import { useQueryNav } from '../lib/nav';
import type { KbArticle, KbOwner, Well } from '../lib/types';

/**
 * What one company knows about itself, and what is known about each of its
 * wells: the Knowledge tab of a company's page.
 *
 * Two shelves side by side in one place, because they answer the same need at
 * two sizes. The company's shelf is for what holds on every one of its wells —
 * how it codes NPT, the limits it sets itself, who signs off what. A well's
 * shelf is for that well alone: its objective, its casing programme, what
 * went wrong on the offset well. The assistant reads both for a question
 * about a well, with the general Knowledge Base, and never another company's.
 *
 * Which shelf is open lives in the URL (?shelf=well-344), so a citation in
 * the assistant's answer can link straight to the article it used.
 */

interface Props {
  /** The company whose page this is; null when its own shelf is not this person's to open. */
  company: { id: number; name: string } | null;
  /** The wells whose shelves this person may open, already narrowed to the company. */
  wells: Well[];
}

/** "well-344" in the URL; anything else is the company's shelf. */
function wellFrom(raw: string | null): number | null {
  const m = /^well-(\d+)$/.exec(raw ?? '');
  return m ? Number(m[1]) : null;
}

export function CompanyKnowledge({ company, wells }: Props) {
  const { params, set } = useQueryNav();

  const asked = wellFrom(params.get('shelf'));
  const well = wells.find((w) => w.id === asked) ?? (company ? null : (wells[0] ?? null));

  // Each shelf's articles as last read: for the numbers on the labels, and
  // for the picture of what the assistant holds. Only the shelves somebody
  // opened are known; the rest show no number.
  const [shelves, setShelves] = useState<Record<string, KbArticle[]>>({});
  const shelfKey = well ? `well-${well.id}` : 'company';
  const keep = useCallback(
    (items: KbArticle[]) => setShelves((have) => ({ ...have, [shelfKey]: items })),
    [shelfKey],
  );

  // Changing shelf closes the open article: its id means nothing elsewhere.
  const openCompany = () => set({ shelf: null, article: null });
  const openWell = (id: number) => set({ shelf: `well-${id}`, article: null });

  if (!company && wells.length === 0) {
    return (
      <p className="content-loading">
        There is no knowledge for you to open here yet: no well has been given to your account.
      </p>
    );
  }

  const owner: KbOwner = well
    ? { kind: 'well', id: well.id }
    : { kind: 'company', id: (company as { id: number }).id };

  const count = (key: string) => (shelves[key] === undefined ? '' : ` (${shelves[key].length})`);

  return (
    <div className="wellsknowledge">
      <div className="kbscope">
        <span className="kbscope-label">Knowledge for</span>

        <div className="kbscope-switch" role="tablist" aria-label="Whose knowledge">
          {company && (
            <button
              type="button"
              role="tab"
              aria-selected={!well}
              className={!well ? 'kbscope-btn is-active' : 'kbscope-btn'}
              onClick={openCompany}
            >
              {company.name}
              <small>every well of the company{count('company')}</small>
            </button>
          )}

          {wells.length > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={Boolean(well)}
              className={well ? 'kbscope-btn is-active' : 'kbscope-btn'}
              onClick={() => openWell((well ?? wells[0]).id)}
            >
              One well
              <small>
                {well ? `${well.name}${count(shelfKey)}` : `${wells.length} to choose from`}
              </small>
            </button>
          )}
        </div>

        {well && wells.length > 1 && (
          <label className="kbscope-well">
            Well
            <select
              className="kbselect"
              value={well.id}
              onChange={(e) => openWell(Number(e.target.value))}
            >
              {wells.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <p className="kbintro">
        {well ? (
          <>
            What is known about <strong>{well.name}</strong> and nowhere else: its objective and
            targets, the casing and mud programme, the hazards expected, what the offset wells
            taught. The assistant uses it only for questions asked on this well.
          </>
        ) : (
          <>
            What holds on every well of <strong>{company?.name}</strong>: how it codes NPT, the
            limits and targets it sets itself, its naming, who signs off what. The assistant uses
            it for questions asked on any of this company's wells, and never for another
            company's.
          </>
        )}{' '}
        A plan or a target written here is quoted as what the company states; measured values
        still come from the documents.
      </p>

      <KnowledgeSpace
        key={`space-${shelfKey}`}
        name={well ? well.name : (company?.name ?? 'the company')}
        well={well}
        wells={wells}
        articles={shelves[shelfKey] ?? null}
        onOpenArticle={(id) => set({ article: String(id) })}
      />

      {/* Keyed by shelf, so a draft on one never appears on another. */}
      <ArticleShelf
        key={shelfKey}
        owner={owner}
        onLoaded={keep}
        hint={
          well
            ? `Pick an article to edit it, or write a new one about ${well.name}. Keep each to one subject: the well's objective, its casing programme, a lesson from an offset well.`
            : `Pick an article to edit it, or write a new one for ${company?.name}. Keep each to one subject: a set of codes, a limit, a naming rule, who to ask.`
        }
        titlePlaceholder={well ? 'Well objective and targets' : 'How NPT is coded here'}
        bodyPlaceholder={
          well
            ? 'Write what a new engineer joining this well should be told on the first day.'
            : 'Write it as you would explain it to somebody new to the company.'
        }
      />
    </div>
  );
}
